import React, { useEffect, useMemo, useState } from 'react';

import { TailRecord, SectionResult, AppMode } from './types';
import { runSection, SECTION_IDS } from './api';
import { login, handleCallback, getUser, AuthUser } from './auth';
import { useTailNumbers } from './hooks/useTailNumbers';
import { errorMessage } from './errors';
import Header from './components/Header';
import ControlsBar from './components/ControlsBar';
import SectionResults from './components/SectionResults';
import './App.css';

/** Split a discrepancy string into individual items when numbered (e.g. "1) ... 2) ..."). */
function parseDiscrepancies(text: string): string[] {
  if (!text) return [];
  const parts = text.split(/(?=\d+\)\s*)/);
  const items = parts.map((part) => part.trim()).filter((part) => part.length > 0);
  return items.length <= 1 ? [text.trim()] : items;
}

/**
 * Build a TailRecord for the current analysis run.
 * Returns null if the inputs are incomplete for the given mode so the caller
 * can short-circuit without running the section loop.
 */
function buildRunRecord(
  mode: AppMode,
  selected: TailRecord | null,
  discrepancies: string[],
  checkedDiscreps: Set<number>,
  searchCarrier: string,
  searchDiscrepancy: string,
): TailRecord | null {
  if (mode === 'inWork') {
    if (!selected || checkedDiscreps.size === 0) return null;
    const selectedTexts = discrepancies
      .filter((_, index) => checkedDiscreps.has(index))
      .join(' ');
    return { ...selected, discrepancy: selectedTexts };
  }

  if (!searchCarrier || !searchDiscrepancy.trim()) return null;
  return {
    tailNumber: 'N/A',
    carrier: searchCarrier,
    ataCode: '',
    discrepancy: searchDiscrepancy.trim(),
    station: '',
  };
}

/** Root application component with auth, mode toggle, and analysis orchestration. */
const App: React.FC = () => {
  const [user, setUser] = useState<AuthUser | null>(getUser());
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [mode, setMode] = useState<AppMode>('inWork');
  const [selected, setSelected] = useState<TailRecord | null>(null);
  const [sections, setSections] = useState<SectionResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [checkedDiscreps, setCheckedDiscreps] = useState<Set<number>>(new Set());
  const [searchCarrier, setSearchCarrier] = useState<string>('');
  const [searchDiscrepancy, setSearchDiscrepancy] = useState<string>('');

  // All tail loading / refresh / cancellation logic lives in the hook.
  const {
    records: tailRecords,
    loading: loadingTails,
    error: loadTailsError,
    refresh: refreshTails,
  } = useTailNumbers(!!user);

  // Surface auth error, per-run error, or tail load error in the results panel.
  const error = authError || runError || loadTailsError;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('code') && !user) {
      setAuthLoading(true);
      handleCallback().then((result) => {
        if (result.status === 'ok') {
          setUser(result.user);
        } else if (result.status === 'error') {
          // Distinct failure reasons surface with distinct copy instead of a
          // silent null; see simp-008 in auth.ts for the reason codes.
          setAuthError(result.message);
        }
        // 'no_code' means this isn't a callback — nothing to do.
        setAuthLoading(false);
      });
    } else if (!user) {
      login();
    }
  }, []); // Run once on mount — intentional empty deps

  const discrepancies = useMemo(
    () => (selected ? parseDiscrepancies(selected.discrepancy) : []),
    [selected],
  );

  /** Reset analysis output state. Shared by mode change and tail selection. */
  const resetResults = (): void => {
    setSections([]);
    setRunError(null);
    setCurrentSection(null);
  };

  const handleModeChange = (newMode: AppMode): void => {
    setMode(newMode);
    resetResults();
  };

  const handleSelect = (tailNumber: string): void => {
    const record = tailRecords.find((rec) => rec.tailNumber === tailNumber) || null;
    setSelected(record);
    resetResults();
    if (record) {
      const parsed = parseDiscrepancies(record.discrepancy);
      setCheckedDiscreps(parsed.length === 1 ? new Set([0]) : new Set());
    }
  };

  const handleToggleDiscrep = (index: number): void => {
    setCheckedDiscreps((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  /** Run analysis sections sequentially. Record construction is factored out. */
  const handleRun = async (): Promise<void> => {
    const record = buildRunRecord(
      mode, selected, discrepancies, checkedDiscreps, searchCarrier, searchDiscrepancy,
    );
    if (!record) return;

    setLoading(true);
    setRunError(null);
    setSections([]);

    for (const sectionId of SECTION_IDS) {
      setCurrentSection(sectionId);
      try {
        const result = await runSection(record, sectionId);
        setSections((prev) => [...prev, result]);
      } catch (err: unknown) {
        const message = errorMessage(err);
        setSections((prev) => [
          ...prev,
          {
            section: sectionId,
            title: `Section ${sectionId}`,
            content: `Error: ${message}`,
          },
        ]);
      }
    }

    setCurrentSection(null);
    setLoading(false);
  };

  /** Derived value: whether the Run button should be enabled. */
  const canRun: boolean = !loading && (
    mode === 'inWork'
      ? !!selected && checkedDiscreps.size > 0
      : !!searchCarrier && searchDiscrepancy.trim().length > 0
  );

  if (authLoading || !user) {
    return (
      <div className="app">
        <Header user={null} />
        <div className="auth-loading">
          {authError ? (
            <div className="error-banner" role="alert">
              &#9888; {authError}
              <button
                type="button"
                className="auth-retry-btn"
                onClick={() => {
                  setAuthError(null);
                  login();
                }}
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              <div className="spinner" />
              <p>Signing in...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header user={user} />
      <div className="main-container">
        <ControlsBar
          mode={mode}
          onModeChange={handleModeChange}
          records={tailRecords}
          selected={selected}
          discrepancies={discrepancies}
          checkedDiscreps={checkedDiscreps}
          onToggleDiscrep={handleToggleDiscrep}
          onSelect={handleSelect}
          onRun={handleRun}
          onRefresh={refreshTails}
          loading={loading}
          loadingTails={loadingTails}
          canRun={canRun}
          searchCarrier={searchCarrier}
          onSearchCarrierChange={setSearchCarrier}
          searchDiscrepancy={searchDiscrepancy}
          onSearchDiscrepancyChange={setSearchDiscrepancy}
        />
        <div className="results-area">
          <SectionResults
            sections={sections}
            loading={loading}
            currentSection={currentSection}
            error={error}
            selectedTail={mode === 'inWork' ? selected?.tailNumber : undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default App;
